import { Keyboard, Pressable, Text, View } from 'react-native'
import { Link, useRouter } from 'expo-router'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { COUNTRY_CODES } from '@/constants/config'
import { Button, FormScrollView } from '@/components/base'
import { FormInput, PhoneInput } from '@/components/form-control'
import { useLoadingIndicator } from '@/components/LoadingIndicator'

export type LoginValues = {
  phoneNumber: {
    code: string
    number: string
  }
  password: string
}

const defaultValues: LoginValues = {
  phoneNumber: {
    code: COUNTRY_CODES.China.dial_code,
    number: ''
  },
  password: ''
}

interface LoginFormProps {
  onSubmit: SubmitHandler<LoginValues>
}

export function LoginForm({ onSubmit }: LoginFormProps) {
  const router = useRouter()
  const { t } = useTranslation()
  const loading = useLoadingIndicator()
  const { control, handleSubmit, setFocus, watch, setError } = useForm<LoginValues>({ defaultValues })
  const password = watch('password')
  const phoneNumber = watch('phoneNumber.number')

  function preOnSubmit(data: LoginValues) {
    Keyboard.dismiss()
    if (phoneNumber.length > 15 || !/^\d+$/.test(phoneNumber)) {
      setError('phoneNumber.number', { message: t('auth:hint.phoneNumberInvalid') })
      return
    }
    const strippedZeroPhoneNumber = Number(data.phoneNumber.number).toString()
    onSubmit({ ...data, phoneNumber: { ...data.phoneNumber, number: strippedZeroPhoneNumber } })
  }

  return (
    <FormScrollView className={'pb-4'}>
      <View className={'gap-y-4 px-4'}>
        <View className={'justify-center pb-3 pt-2'}>
          <Text className={'text-2xl font-semibold text-text-primary'}>{t('auth:login')}</Text>
        </View>
        <View className={'gap-y-2'}>
          <PhoneInput
            control={control}
            name={{ code: 'phoneNumber.code', number: 'phoneNumber.number' }}
            label={t('auth:label:phoneNumber')}
            placeholder={t('auth:placeholder.phoneNumber')}
            textContentType={'none'}
            autoCorrect={false}
            returnKeyType={'next'}
            onSubmitEditing={() => setFocus('password')}
          />
          <FormInput
            control={control}
            name={'password'}
            label={t('auth:label.password')}
            labelRightElement={
              <Pressable onPress={() => router.navigate('/forgot-password')}>
                <Text className={'text-sm font-medium text-info'}>{t('auth:forgetPassword')}</Text>
              </Pressable>
            }
            type={'password'}
            placeholder={t('auth:placeholder.password')}
            textContentType={'password'}
            autoCapitalize={'none'}
            autoCorrect={false}
            returnKeyType={'go'}
            onSubmitEditing={handleSubmit(preOnSubmit)}
          />
        </View>
        <View className={'gap-y-10'}>
          <Button
            testID={'login-submit'}
            onPress={handleSubmit(preOnSubmit)}
            disabled={!password || !phoneNumber || loading.isLoading}
          >
            {t('auth:login')}
          </Button>
          <Text className={'text-sm text-text-secondary'}>
            <Trans
              i18nKey={'auth:noAccount'}
              components={[<Link key={0} className={'font-medium text-info'} href={'/register'} />]}
            />
          </Text>
        </View>
      </View>
    </FormScrollView>
  )
}
